import React, { useState, useEffect } from 'react';
import {
  getTwilioNumbers, configureTwilioWebhook,
  getTalkliveInstances, createTalkliveInstance, updateTalkliveInstance, deleteTalkliveInstance
} from '../services/api';
import SearchableDropdown from './SearchableDropdown';

const TIMEZONES = [
  'America/Montreal', 'America/Toronto', 'America/Vancouver',
  'America/Edmonton', 'America/Winnipeg', 'America/Halifax',
  'America/St_Johns', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles'
];

const CAPTURE_TOOLS = [
  { value: 'capture_lead', label: 'Capture Lead' },
  { value: 'capture_activix', label: 'Capture Activix' },
  { value: 'capture_talklive', label: 'Capture TalkLive' },
  { value: 'capture_adf', label: 'Capture ADF' },
  { value: 'capture_talklive_activix', label: 'TalkLive + Activix' },
];

const DAY_LABELS = {
  monday: { fr: 'Lundi', en: 'Monday' },
  tuesday: { fr: 'Mardi', en: 'Tuesday' },
  wednesday: { fr: 'Mercredi', en: 'Wednesday' },
  thursday: { fr: 'Jeudi', en: 'Thursday' },
  friday: { fr: 'Vendredi', en: 'Friday' },
  saturday: { fr: 'Samedi', en: 'Saturday' },
  sunday: { fr: 'Dimanche', en: 'Sunday' },
};
const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function canonicalDayKey(key) {
  const s = String(key).toLowerCase();
  if (DAYS_ORDER.includes(s)) return s;
  if (/^[0-6]$/.test(s)) return DAYS_ORDER[Number(s)];
  return null;
}

function normalizeSchedule(schedule) {
  if (!schedule || typeof schedule !== 'object') return {};
  const numeric = {};
  const named = {};
  for (const [key, value] of Object.entries(schedule)) {
    const canon = canonicalDayKey(key);
    if (!canon || !value) continue;
    if (DAYS_ORDER.includes(String(key).toLowerCase())) named[canon] = value;
    else numeric[canon] = value;
  }
  return { ...numeric, ...named };
}

function instanceSchedule(instance) {
  const existing = instance.after_hours?.business_hours?.schedule;
  if (existing != null && typeof existing === 'object') {
    return normalizeSchedule(existing);
  }
  return { ...defaultInstanceConfig.after_hours.business_hours.schedule };
}

const defaultInstanceConfig = {
  instance_id: '',
  label: '',
  twilio_number: '',
  instructions_id: '',
  enabled: true,
  capture_mode: 'ai',
  talklive_tool: '',
  auto_capture_name: 'Appelant Talklive',
  instructions: '',
  sms_template_fr: '',
  sms_template_en: '',
  voice_message_fr: '',
  voice_message_en: '',
  follow_up: {
    enabled: false,
    timezone: 'America/Montreal',
    business_hours_start: 8,
    business_hours_end: 20,
    steps: []
  },
  after_hours: {
    enabled: false,
    business_hours: {
      timezone: 'America/Montreal',
      schedule: {
        monday: { open: 8, close: 20 },
        tuesday: { open: 8, close: 20 },
        wednesday: { open: 8, close: 20 },
        thursday: { open: 8, close: 20 },
        friday: { open: 8, close: 20 },
        saturday: { open: 9, close: 17 },
      },
    },
    sms_template_fr: '',
    sms_template_en: '',
    voice_message_fr: '',
    voice_message_en: '',
  },
  emails: [],
  adf_emails: [],
  include_inventory: false,
  voicemail: { enabled: false }
};

const TalkliveSetupModal = ({
  isOpen,
  onClose,
  assistantId,
  assistantName,
  instructionOptions,
  onSave
}) => {
  // View: 'list' | 'form'
  const [view, setView] = useState('list');
  const [instances, setInstances] = useState([]);
  const [editingInstance, setEditingInstance] = useState(null); // null = new instance
  const [formData, setFormData] = useState({ ...defaultInstanceConfig });
  const [availableTwilioNumbers, setAvailableTwilioNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !assistantId) return;

    setView('list');
    setEditingInstance(null);
    setDeleteConfirm(null);
    setError('');

    const loadData = async () => {
      setLoading(true);
      try {
        const [instancesRes, twilioRes] = await Promise.allSettled([
          getTalkliveInstances(assistantId),
          getTwilioNumbers(),
        ]);

        // Load instances
        if (instancesRes.status === 'fulfilled' && instancesRes.value.data) {
          const data = instancesRes.value.data;
          setInstances(Array.isArray(data) ? data : (data.instances || []));
        } else {
          setInstances([]);
        }

        // Load Twilio numbers
        if (twilioRes.status === 'fulfilled' && twilioRes.value.data) {
          const numbers = twilioRes.value.data.numbers || twilioRes.value.data || [];
          setAvailableTwilioNumbers(Array.isArray(numbers) ? numbers : []);
        }
      } catch (err) {
        console.error('Error loading TalkLive data:', err);
        setError('Failed to load configuration.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, assistantId]);

  const refreshInstances = async () => {
    try {
      const res = await getTalkliveInstances(assistantId);
      const data = res.data;
      const list = Array.isArray(data) ? data : (data.instances || []);
      setInstances(list);
      if (onSave) onSave(list);
    } catch (err) {
      console.error('Error refreshing instances:', err);
    }
  };

  // --- Instance form handlers ---

  const openNewInstanceForm = () => {
    setEditingInstance(null);
    setFormData({ ...defaultInstanceConfig });
    setError('');
    setView('form');
  };

  const duplicateInstance = (instance) => {
    setEditingInstance(null); // null = new instance (not editing)
    setFormData({
      ...defaultInstanceConfig,
      ...instance,
      instance_id: '', // will be auto-generated from new label
      label: `${instance.label || ''} (copy)`,
      twilio_number: '', // must pick a different number
      emails: instance.emails || [],
      adf_emails: instance.adf_emails || [],
      follow_up: {
        ...defaultInstanceConfig.follow_up,
        ...(instance.follow_up || {})
      },
      after_hours: {
        ...defaultInstanceConfig.after_hours,
        ...(instance.after_hours || {}),
        business_hours: {
          ...defaultInstanceConfig.after_hours.business_hours,
          ...(instance.after_hours?.business_hours || {}),
          schedule: instanceSchedule(instance)
        }
      }
    });
    setError('');
    setView('form');
  };

  const openEditInstanceForm = (instance) => {
    setEditingInstance(instance);
    setFormData({
      ...defaultInstanceConfig,
      ...instance,
      emails: instance.emails || [],
      adf_emails: instance.adf_emails || [],
      follow_up: {
        ...defaultInstanceConfig.follow_up,
        ...(instance.follow_up || {})
      },
      after_hours: {
        ...defaultInstanceConfig.after_hours,
        ...(instance.after_hours || {}),
        business_hours: {
          ...defaultInstanceConfig.after_hours.business_hours,
          ...(instance.after_hours?.business_hours || {}),
          schedule: instanceSchedule(instance)
        }
      }
    });
    setError('');
    setView('form');
  };

  const handleInstanceSave = async () => {
    if (!formData.label.trim()) {
      setError('Label is required.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const instanceId = editingInstance
        ? editingInstance.instance_id
        : 'tl_' + formData.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

      const payload = {
        instance_id: instanceId,
        label: formData.label,
        twilio_number: formData.twilio_number || null,
        instructions_id: formData.instructions_id || null,
        enabled: formData.enabled,
        capture_mode: formData.capture_mode,
        talklive_tool: formData.talklive_tool || null,
        auto_capture_name: formData.auto_capture_name || 'Appelant Talklive',
        instructions: formData.instructions || null,
        sms_template_fr: formData.sms_template_fr || null,
        sms_template_en: formData.sms_template_en || null,
        voice_message_fr: formData.voice_message_fr ?? '',
        voice_message_en: formData.voice_message_en ?? '',
        follow_up: formData.follow_up?.enabled ? formData.follow_up : { enabled: false },
        after_hours: formData.after_hours?.enabled ? formData.after_hours : { enabled: false },
        emails: formData.emails?.length > 0 ? formData.emails : null,
        adf_emails: formData.adf_emails?.length > 0 ? formData.adf_emails : null,
        include_inventory: formData.include_inventory || false,
        voicemail: { enabled: !!formData.voicemail?.enabled },
      };

      if (editingInstance) {
        await updateTalkliveInstance(assistantId, instanceId, payload);
      } else {
        await createTalkliveInstance(assistantId, payload);
      }

      // Configure Twilio webhook if a number is selected
      if (formData.twilio_number) {
        const matchedNumber = availableTwilioNumbers.find(
          n => n.phone_number === formData.twilio_number
        );
        if (matchedNumber) {
          try {
            await configureTwilioWebhook({ phone_number_sid: matchedNumber.sid });
          } catch (webhookErr) {
            console.warn('Webhook config failed (non-blocking):', webhookErr);
          }
        }
      }

      await refreshInstances();
      setView('list');
    } catch (err) {
      console.error('Error saving instance:', err);
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || JSON.stringify(d)).join(', '));
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to save instance. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInstanceDelete = async (instanceId) => {
    try {
      await deleteTalkliveInstance(assistantId, instanceId);
      setDeleteConfirm(null);
      await refreshInstances();
    } catch (err) {
      console.error('Error deleting instance:', err);
      setError('Failed to delete instance.');
    }
  };

  // --- Shared form field helpers ---

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateFollowUp = (field, value) => {
    setFormData(prev => ({
      ...prev,
      follow_up: { ...prev.follow_up, [field]: value }
    }));
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      follow_up: {
        ...prev.follow_up,
        steps: [...(prev.follow_up.steps || []), { delay_minutes: 60, alert_context: '' }]
      }
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      follow_up: {
        ...prev.follow_up,
        steps: prev.follow_up.steps.map((step, i) =>
          i === index ? { ...step, [field]: value } : step
        )
      }
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      follow_up: {
        ...prev.follow_up,
        steps: prev.follow_up.steps.filter((_, i) => i !== index)
      }
    }));
  };

  if (!isOpen) return null;

  // ==================== RENDER HELPERS ====================

  const captureToolOptions = [
    { key: '— Default (first capture tool) —', value: '', valueDisplay: 'Clear selection' },
    ...CAPTURE_TOOLS.map((t) => ({ key: t.label, value: t.value, valueDisplay: t.value })),
  ];

  const instructionSelectOptions = [
    { key: '— Select instructions —', value: '', valueDisplay: 'Clear selection' },
    ...(instructionOptions || []).map((id) => ({ key: id, value: id })),
  ];

  const baseTwilioOptions = [
    { key: '— Select a Twilio number —', value: '', valueDisplay: 'Clear selection' },
    ...availableTwilioNumbers.map((num) => ({
      key: num.phone_number,
      value: num.phone_number,
      valueDisplay: num.friendly_name || '',
    })),
  ];

  const twilioOptionsFor = (currentNumber) => {
    if (
      currentNumber &&
      !availableTwilioNumbers.find((n) => n.phone_number === currentNumber)
    ) {
      return [
        ...baseTwilioOptions,
        { key: currentNumber, value: currentNumber, valueDisplay: 'current' },
      ];
    }
    return baseTwilioOptions;
  };

  const timezoneOptions = TIMEZONES.map((tz) => ({ key: tz, value: tz }));

  const renderInstanceFormFields = (config, onUpdate, onUpdateFollowUp, onAddStep, onUpdateStep, onRemoveStep, twilioNumber, onTwilioChange, instructionsIdVal, onInstructionsChange, isInstanceForm) => (
    <>
      {/* Instance Info (only for multi-instance form) */}
      {isInstanceForm && (
        <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
          <legend style={{ fontWeight: 600, padding: '0 8px' }}>Instance Info</legend>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Label *</label>
            <input
              type="text"
              value={config.label || ''}
              onChange={(e) => onUpdate('label', e.target.value)}
              placeholder="e.g. Ventes, Service, Pieces"
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Instance ID (auto-generated)</label>
            <input
              type="text"
              value={editingInstance ? config.instance_id : (config.label ? 'tl_' + config.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') : '')}
              disabled
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', boxSizing: 'border-box', background: '#f5f5f5', color: '#888' }}
            />
          </div>
        </fieldset>
      )}

      {/* Activation */}
      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <legend style={{ fontWeight: 600, padding: '0 8px' }}>Activation</legend>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={config.enabled || false}
              onChange={(e) => onUpdate('enabled', e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontWeight: 500 }}>Enable TalkLive</span>
          </label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={config.include_inventory || false}
              onChange={(e) => onUpdate('include_inventory', e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontWeight: 500 }}>Inclure l'inventaire</span>
          </label>
          <span style={{ fontSize: 11, color: '#888', marginLeft: 26 }}>Ajoute l'inventaire du concessionnaire dans le prompt AI</span>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Twilio Number (TalkLive)</label>
          <SearchableDropdown
            options={twilioOptionsFor(twilioNumber)}
            value={twilioNumber || ''}
            onChange={onTwilioChange}
            placeholder="— Select a Twilio number —"
          />
          {/* Inline warning: number already used by another instance in this dealer */}
          {twilioNumber && isInstanceForm && (() => {
            const currentInstanceId = editingInstance?.instance_id;
            const conflict = instances.find(
              i => i.twilio_number === twilioNumber && i.instance_id !== currentInstanceId
            );
            if (conflict) {
              return (
                <div style={{
                  background: '#fff3cd', border: '1px solid #ffc107', color: '#856404',
                  padding: '6px 10px', borderRadius: 4, marginTop: 6, fontSize: 12
                }}>
                  This number is already used by instance "{conflict.label || conflict.instance_id}".
                </div>
              );
            }
            return null;
          })()}
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>TalkLive Instructions ID</label>
          <SearchableDropdown
            options={instructionSelectOptions}
            value={instructionsIdVal || ''}
            onChange={onInstructionsChange}
            placeholder="— Select instructions —"
          />
        </div>
      </fieldset>

      {/* Capture Mode */}
      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <legend style={{ fontWeight: 600, padding: '0 8px' }}>Capture Mode</legend>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Mode</label>
          <div style={{ display: 'flex', gap: 16 }}>
            {['auto', 'ai', 'disabled'].map((mode) => (
              <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="capture_mode"
                  value={mode}
                  checked={config.capture_mode === mode}
                  onChange={() => onUpdate('capture_mode', mode)}
                />
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </label>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Capture Tool</label>
          <SearchableDropdown
            options={captureToolOptions}
            value={config.talklive_tool || ''}
            onChange={(v) => onUpdate('talklive_tool', v)}
            placeholder="— Default (first capture tool) —"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Auto-capture Name</label>
          <input
            type="text"
            value={config.auto_capture_name || ''}
            onChange={(e) => onUpdate('auto_capture_name', e.target.value)}
            placeholder="Appelant Talklive"
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
          />
        </div>
      </fieldset>

      {/* Notification Emails Override */}
      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <legend style={{ fontWeight: 600, padding: '0 8px' }}>Notification Emails (Override)</legend>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
            Courriels de notification
          </label>
          <p style={{ fontSize: 11, color: '#888', marginBottom: 8, marginTop: 0 }}>
            Si configurés, ces courriels reçoivent les notifications à la place des courriels du dealer. Un par ligne.
          </p>
          <textarea
            value={(config.emails || []).join('\n')}
            onChange={(e) => {
              const lines = e.target.value.split('\n').map(l => l.trim()).filter(Boolean);
              onUpdate('emails', lines);
            }}
            placeholder="email1@example.com&#10;email2@example.com"
            rows={3}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12 }}
          />
        </div>
      </fieldset>

      {/* ADF Emails Override — visible seulement si talklive_tool === 'capture_adf' */}
      {config.talklive_tool === 'capture_adf' && (
        <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
          <legend style={{ fontWeight: 600, padding: '0 8px' }}>ADF Emails (Override)</legend>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>
              Courriels ADF
            </label>
            <p style={{ fontSize: 11, color: '#888', marginBottom: 8, marginTop: 0 }}>
              Si configurés, les leads ADF/XML sont envoyés à ces emails au lieu des ADF emails
              généraux du dealer assistant. Laisser vide pour utiliser les ADF emails par défaut.
            </p>
            <textarea
              value={(config.adf_emails || []).join('\n')}
              onChange={(e) => {
                const lines = e.target.value.split('\n').map(l => l.trim()).filter(Boolean);
                onUpdate('adf_emails', lines);
              }}
              placeholder="adf-crm@dealer.com&#10;adf-dms@dealer.com"
              rows={3}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        </fieldset>
      )}

      {/* SMS Templates */}
      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <legend style={{ fontWeight: 600, padding: '0 8px' }}>SMS Templates</legend>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Template FR</label>
          <textarea
            value={config.sms_template_fr || ''}
            onChange={(e) => onUpdate('sms_template_fr', e.target.value)}
            placeholder="Bonjour! Desolé d'avoir manqué votre appel chez {dealer_name}. Comment puis-je vous aider?"
            rows={3}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Template EN</label>
          <textarea
            value={config.sms_template_en || ''}
            onChange={(e) => onUpdate('sms_template_en', e.target.value)}
            placeholder="Hi! Sorry we missed your call at {dealer_name}. How can I help you?"
            rows={3}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
      </fieldset>

      {/* Voice Messages */}
      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <legend style={{ fontWeight: 600, padding: '0 8px' }}>Voice Messages</legend>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!config.voicemail?.enabled}
              onChange={(e) => onUpdate('voicemail', { enabled: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontWeight: 500 }}>Enable voicemail (record after greeting)</span>
          </label>
          {config.voicemail?.enabled && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#92400e' }}>
              Rewrite the greeting so it mentions the beep. No second greeting file is generated.
            </p>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Voice Message FR</label>
          <textarea
            value={config.voice_message_fr || ''}
            onChange={(e) => onUpdate('voice_message_fr', e.target.value)}
            rows={2}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Voice Message EN</label>
          <textarea
            value={config.voice_message_en || ''}
            onChange={(e) => onUpdate('voice_message_en', e.target.value)}
            rows={2}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
      </fieldset>

      {/* Advanced Settings (collapsible) */}
      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setAdvancedOpen(prev => !prev)}
          style={{
            width: '100%', padding: '10px 16px', borderRadius: 6,
            border: '1px solid #ddd', background: '#f8f9fa', cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: 14, fontWeight: 600, color: '#333'
          }}
        >
          <span>Advanced Settings</span>
          <span style={{ fontSize: 12, color: '#888' }}>{advancedOpen ? '▲ Hide' : '▼ Show'}</span>
        </button>

        {advancedOpen && (
          <div style={{ marginTop: 12 }}>
            {/* Follow-ups */}
            <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
              <legend style={{ fontWeight: 600, padding: '0 8px' }}>Follow-up Sequences</legend>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.follow_up?.enabled || false}
                    onChange={(e) => onUpdateFollowUp('enabled', e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontWeight: 500 }}>Enable Follow-ups</span>
                </label>
              </div>

              {config.follow_up?.enabled && (
                <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Timezone</label>
                      <SearchableDropdown
                        options={timezoneOptions}
                        value={config.follow_up.timezone || 'America/Montreal'}
                        onChange={(v) => onUpdateFollowUp('timezone', v || 'America/Montreal')}
                        placeholder="Select timezone"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Start</label>
                      <input
                        type="number"
                        min={0} max={23}
                        value={config.follow_up.business_hours_start ?? 8}
                        onChange={(e) => onUpdateFollowUp('business_hours_start', parseInt(e.target.value, 10))}
                        style={{ width: 70, padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>End</label>
                      <input
                        type="number"
                        min={0} max={23}
                        value={config.follow_up.business_hours_end ?? 20}
                        onChange={(e) => onUpdateFollowUp('business_hours_end', parseInt(e.target.value, 10))}
                        style={{ width: 70, padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>Steps</div>
                  {(config.follow_up.steps || []).map((step, index) => (
                    <div key={index} style={{
                      display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8,
                      background: '#f9f9f9', padding: '8px 12px', borderRadius: 4
                    }}>
                      <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Delay (min)</label>
                        <input
                          type="number"
                          min={1}
                          value={step.delay_minutes}
                          onChange={(e) => onUpdateStep(index, 'delay_minutes', parseInt(e.target.value, 10) || 1)}
                          style={{ width: 80, padding: 6, borderRadius: 4, border: '1px solid #ccc' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, color: '#666' }}>Context (optional)</label>
                        <input
                          type="text"
                          value={step.alert_context || ''}
                          onChange={(e) => onUpdateStep(index, 'alert_context', e.target.value)}
                          placeholder="Custom instruction for this step..."
                          style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveStep(index)}
                        style={{
                          background: '#ff4444', color: 'white', border: 'none',
                          borderRadius: 4, padding: '6px 10px', cursor: 'pointer',
                          marginTop: 16, fontSize: 13
                        }}
                      >Del</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={onAddStep}
                    style={{
                      background: '#007bff', color: 'white', border: 'none',
                      borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 13
                    }}
                  >+ Add Step</button>
                </>
              )}
            </fieldset>

            {/* After-Hours Messaging */}
            <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
              <legend style={{ fontWeight: 600, padding: '0 8px' }}>After-Hours Messaging</legend>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.after_hours?.enabled || false}
                    onChange={(e) => onUpdate('after_hours', { ...(config.after_hours || defaultInstanceConfig.after_hours), enabled: e.target.checked })}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontWeight: 500 }}>Enable different messages for after hours</span>
                </label>
                <span style={{ fontSize: 11, color: '#888', marginLeft: 26, display: 'block', marginTop: 2 }}>
                  Messages diff&eacute;rents pour les heures de fermeture
                </span>
              </div>

              {config.after_hours?.enabled && (() => {
                const ah = config.after_hours || defaultInstanceConfig.after_hours;
                const schedule = ah.business_hours?.schedule || {};

                const updateAfterHoursField = (field, value) => {
                  onUpdate('after_hours', { ...ah, [field]: value });
                };

                const updateScheduleDay = (day, field, value) => {
                  const currentDay = schedule[day] || { open: 8, close: 20 };
                  onUpdate('after_hours', {
                    ...ah,
                    business_hours: {
                      ...ah.business_hours,
                      schedule: {
                        ...schedule,
                        [day]: { ...currentDay, [field]: value }
                      }
                    }
                  });
                };

                const toggleDay = (day) => {
                  const newSchedule = { ...schedule };
                  if (newSchedule[day]) {
                    delete newSchedule[day];
                  } else {
                    newSchedule[day] = { open: 8, close: 20 };
                  }
                  onUpdate('after_hours', {
                    ...ah,
                    business_hours: {
                      ...ah.business_hours,
                      schedule: newSchedule
                    }
                  });
                };

                const hourOptions = [];
                for (let h = 0; h <= 23.5; h += 0.5) {
                  hourOptions.push(h);
                }

                return (
                  <>
                    {/* Timezone */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Timezone</label>
                      <SearchableDropdown
                        options={timezoneOptions}
                        value={ah.business_hours?.timezone || 'America/Montreal'}
                        onChange={(v) => onUpdate('after_hours', {
                          ...ah,
                          business_hours: { ...ah.business_hours, timezone: v || 'America/Montreal' }
                        })}
                        placeholder="Select timezone"
                      />
                    </div>

                    {/* Business Hours Schedule */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 13 }}>Business Hours Schedule</label>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Day</th>
                            <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 500 }}>Open</th>
                            <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 500 }}>Open Hour</th>
                            <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 500 }}>Close Hour</th>
                          </tr>
                        </thead>
                        <tbody>
                          {DAYS_ORDER.map((day) => {
                            const isOpen = !!schedule[day];
                            const dayData = schedule[day] || { open: 8, close: 20 };
                            return (
                              <tr key={day} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '6px 8px' }}>
                                  {DAY_LABELS[day].fr} / {DAY_LABELS[day].en}
                                </td>
                                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                  <input
                                    type="checkbox"
                                    checked={isOpen}
                                    onChange={() => toggleDay(day)}
                                    style={{ width: 16, height: 16 }}
                                  />
                                </td>
                                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                  <select
                                    value={dayData.open}
                                    onChange={(e) => updateScheduleDay(day, 'open', parseFloat(e.target.value))}
                                    disabled={!isOpen}
                                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', opacity: isOpen ? 1 : 0.4 }}
                                  >
                                    {hourOptions.map((h) => (
                                      <option key={h} value={h}>{Math.floor(h)}:{h % 1 === 0 ? '00' : '30'}</option>
                                    ))}
                                  </select>
                                </td>
                                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                  <select
                                    value={dayData.close}
                                    onChange={(e) => updateScheduleDay(day, 'close', parseFloat(e.target.value))}
                                    disabled={!isOpen}
                                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', opacity: isOpen ? 1 : 0.4 }}
                                  >
                                    {hourOptions.map((h) => (
                                      <option key={h} value={h}>{Math.floor(h)}:{h % 1 === 0 ? '00' : '30'}</option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* After-Hours SMS Templates */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>SMS Template FR (after hours)</label>
                      <textarea
                        value={ah.sms_template_fr || ''}
                        onChange={(e) => updateAfterHoursField('sms_template_fr', e.target.value)}
                        placeholder="Bonjour! Nous sommes actuellement ferm&eacute;s. Nous vous r&eacute;pondrons d&egrave;s l'ouverture."
                        rows={3}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>SMS Template EN (after hours)</label>
                      <textarea
                        value={ah.sms_template_en || ''}
                        onChange={(e) => updateAfterHoursField('sms_template_en', e.target.value)}
                        placeholder="Hi! We are currently closed. We will get back to you when we reopen."
                        rows={3}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* After-Hours Voice Messages */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Voice Message FR (after hours)</label>
                      <textarea
                        value={ah.voice_message_fr || ''}
                        onChange={(e) => updateAfterHoursField('voice_message_fr', e.target.value)}
                        placeholder="Merci de votre appel. Nous sommes pr&eacute;sentement ferm&eacute;s. Vous recevrez un texto pour continuer."
                        rows={2}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Voice Message EN (after hours)</label>
                      <textarea
                        value={ah.voice_message_en || ''}
                        onChange={(e) => updateAfterHoursField('voice_message_en', e.target.value)}
                        placeholder="Thank you for your call. We are currently closed. You will receive a text to continue."
                        rows={2}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>

                    <p style={{ fontSize: 11, color: '#888', margin: '8px 0 0 0' }}>
                      When a call comes in outside business hours, the after-hours SMS and voice messages will be used instead of the default ones.
                      Days without the "Open" checkbox are treated as closed all day.
                    </p>
                  </>
                );
              })()}
            </fieldset>
          </div>
        )}
      </div>

      {/* Custom Instructions */}
      <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16 }}>
        <legend style={{ fontWeight: 600, padding: '0 8px' }}>Custom Instructions (optional)</legend>
        <textarea
          value={config.instructions || ''}
          onChange={(e) => onUpdate('instructions', e.target.value)}
          placeholder="Additional instructions injected into the TalkLive prompt..."
          rows={4}
          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
        />
      </fieldset>
    </>
  );

  // ==================== LIST VIEW ====================

  const renderListView = () => (
    <>
      {error && (
        <div style={{
          background: '#fee', border: '1px solid #fcc', color: '#c33',
          padding: '8px 12px', borderRadius: 4, marginBottom: 16
        }}>{error}</div>
      )}

      {instances.length === 0 && (
        <div style={{ textAlign: 'center', padding: '30px 0', color: '#888' }}>
          No TalkLive instances configured yet.
        </div>
      )}

      {instances.map((inst) => (
        <div key={inst.instance_id} style={{
          border: '1px solid #ddd', borderRadius: 6, padding: '12px 16px',
          marginBottom: 10, background: '#fafafa'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                background: inst.enabled ? '#28a745' : '#ccc'
              }} />
              <strong style={{ fontSize: 14 }}>{inst.label || inst.instance_id}</strong>
              {inst.twilio_number && (
                <span style={{ fontSize: 13, color: '#555' }}>{inst.twilio_number}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => openEditInstanceForm(inst)}
                style={{
                  padding: '4px 12px', borderRadius: 4, border: '1px solid #007bff',
                  background: 'white', color: '#007bff', cursor: 'pointer', fontSize: 12
                }}
              >Edit</button>
              <button
                type="button"
                onClick={() => duplicateInstance(inst)}
                style={{
                  padding: '4px 12px', borderRadius: 4, border: '1px solid #6c757d',
                  background: 'white', color: '#6c757d', cursor: 'pointer', fontSize: 12
                }}
              >Duplicate</button>
              {deleteConfirm === inst.instance_id ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => handleInstanceDelete(inst.instance_id)}
                    style={{
                      padding: '4px 10px', borderRadius: 4, border: 'none',
                      background: '#dc3545', color: 'white', cursor: 'pointer', fontSize: 12
                    }}
                  >Confirm</button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    style={{
                      padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc',
                      background: 'white', cursor: 'pointer', fontSize: 12
                    }}
                  >Cancel</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(inst.instance_id)}
                  style={{
                    padding: '4px 12px', borderRadius: 4, border: '1px solid #dc3545',
                    background: 'white', color: '#dc3545', cursor: 'pointer', fontSize: 12
                  }}
                >Delete</button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#777', marginTop: 4, marginLeft: 20 }}>
            Mode: {inst.capture_mode || 'ai'} | Follow-up: {inst.follow_up?.enabled ? 'ON' : 'OFF'} | After-hours: {inst.after_hours?.enabled ? 'ON' : 'OFF'}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={openNewInstanceForm}
        style={{
          padding: '8px 16px', borderRadius: 4, border: '2px dashed #007bff',
          background: 'white', color: '#007bff', cursor: 'pointer', fontSize: 13,
          width: '100%', marginTop: 4
        }}
      >+ Add Instance</button>
    </>
  );

  // ==================== FORM VIEW (Instance) ====================

  const renderFormView = () => (
    <>
      {error && (
        <div style={{
          background: '#fee', border: '1px solid #fcc', color: '#c33',
          padding: '8px 12px', borderRadius: 4, marginBottom: 16
        }}>{error}</div>
      )}

      {renderInstanceFormFields(
        formData,
        updateFormField,
        updateFollowUp,
        addStep,
        updateStep,
        removeStep,
        formData.twilio_number || '',
        (val) => updateFormField('twilio_number', val),
        formData.instructions_id || '',
        (val) => updateFormField('instructions_id', val),
        true
      )}
    </>
  );

  // ==================== MAIN RENDER ====================

  const headerTitle = view === 'list'
    ? `TalkLive Instances — ${assistantName || assistantId}`
    : editingInstance
      ? `Edit Instance — ${editingInstance.label || editingInstance.instance_id}`
      : `New Instance — ${assistantName || assistantId}`;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: 'white', borderRadius: 8, width: '700px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #eee',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {view === 'form' && (
              <button
                onClick={() => { setView('list'); setError(''); }}
                style={{
                  background: 'none', border: 'none', fontSize: 14, cursor: 'pointer',
                  color: '#007bff', padding: 0
                }}
              >&larr; Back to List</button>
            )}
            <h2 style={{ margin: 0, fontSize: 18 }}>{headerTitle}</h2>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666'
          }}>X</button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading configuration...</div>
          ) : (
            <>
              {view === 'list' && renderListView()}
              {view === 'form' && renderFormView()}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 24px', borderTop: '1px solid #eee',
        }}>
          {view === 'form' && error && (
            <div style={{
              background: '#fee', border: '1px solid #fcc', color: '#c33',
              padding: '8px 12px', borderRadius: 4, marginBottom: 10, fontSize: 13
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          {view === 'list' && (
            <button
              onClick={onClose}
              style={{
                padding: '8px 20px', borderRadius: 4, border: '1px solid #ccc',
                background: 'white', cursor: 'pointer', fontSize: 14
              }}
            >Close</button>
          )}
          {view === 'form' && (
            <>
              <button
                onClick={() => { setView('list'); setError(''); }}
                disabled={saving}
                style={{
                  padding: '8px 20px', borderRadius: 4, border: '1px solid #ccc',
                  background: 'white', cursor: 'pointer', fontSize: 14
                }}
              >Cancel</button>
              <button
                onClick={handleInstanceSave}
                disabled={saving || loading}
                style={{
                  padding: '8px 20px', borderRadius: 4, border: 'none',
                  background: saving ? '#999' : '#007bff', color: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14
                }}
              >{saving ? 'Saving...' : 'Save Instance'}</button>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalkliveSetupModal;
